import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PencilSimple } from 'phosphor-react';

// changed by Cursor: restyled layout to match compact mock
const WritingPracticeButton = ({ level, completed, total, onStart, variant = 'subcourse' }) => {
  const isLesson = variant === 'lesson';
  const isLoading = level === null || completed === null;
  const progressPercentage = isLoading ? 0 : (total ? Math.min(100, Math.max(0, (completed / total) * 100)) : 0);
  const progressValue = Math.round(progressPercentage);
  const [isHovered, setIsHovered] = useState(false);

  const barGradient = isLesson
    ? 'linear-gradient(90deg, #fb923c 0%, #f97316 50%, #ea580c 100%)'
    : 'linear-gradient(90deg, #12766d 0%, #236463 50%, #1d4746 100%)';

  return (
    <motion.div
      className={`writing-practice-card${isLesson ? ' lesson-variant' : ''} bg-gradient-to-br from-white to-[#f8fffe] dark:from-[#1e293b] dark:to-[#1a2332] rounded-[10px] max-md:rounded-xl p-[6px_10px] max-md:p-[10px_12px] shadow-[0_2px_8px_rgba(18,118,109,0.08),0_1px_3px_rgba(0,0,0,0.05)] max-md:shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.3),0_1px_3px_rgba(0,0,0,0.2)] flex flex-col gap-1 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] flex-1 min-w-0 min-h-[160px] h-full border-[1.5px] ${isLesson ? 'border-[rgba(251,146,60,0.2)] dark:border-[rgba(251,146,60,0.3)] hover:border-[rgba(251,146,60,0.4)] dark:hover:border-[rgba(251,146,60,0.5)] hover:shadow-[0_6px_16px_rgba(251,146,60,0.12),0_2px_6px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_6px_16px_rgba(0,0,0,0.4),0_2px_6px_rgba(0,0,0,0.3)]' : 'border-[rgba(18,118,109,0.2)] dark:border-[rgba(18,118,109,0.3)] hover:border-[rgba(18,118,109,0.4)] dark:hover:border-[rgba(18,118,109,0.5)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.12),0_2px_6px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_6px_16px_rgba(0,0,0,0.4),0_2px_6px_rgba(0,0,0,0.3)]'} relative overflow-hidden touch-action-pan-y`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <motion.div
        className="absolute top-0 left-0 right-0 h-[4px] rounded-t-[10px] max-md:rounded-t-xl origin-top pointer-events-none"
        style={{ background: barGradient }}
        initial={false}
        animate={{
          scaleY: isHovered ? 1 : 0,
          opacity: isHovered ? 1 : 0,
        }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      />
      <div className="flex items-center justify-between -mb-[3px] pt-px">
        <div className="flex items-center gap-[5px]">
          <PencilSimple size={14} weight="regular" />
          <span className="text-[15px] max-md:text-[13px] font-semibold text-[var(--text-primary)]">Writing Practice</span>
        </div>
      </div>
      <div className={`h-px flex-shrink-0 ${isLesson ? 'bg-[rgba(251,146,60,0.15)]' : 'bg-[rgba(18,118,109,0.15)]'} dark:bg-white/5 -mx-1`} />
      <div className="flex items-center gap-[3px] max-md:gap-1 flex-1">
        <div className="flex flex-col gap-0.5 min-w-[40px] pr-2">
          <span className={`text-[11px] max-md:text-[10px] font-semibold tracking-[0.4px] uppercase ${isLesson ? 'text-[#f97316]' : 'text-[var(--accent-teal)]'}`}>Level</span>
          <div className="flex items-baseline gap-[2px]">
            <div className={`text-3xl font-bold text-[var(--text-primary)] leading-none ${isLoading ? 'animate-blink' : ''}`}>
              {isLoading ? '--' : level}
            </div>
            <span className="text-[11px] text-[var(--text-primary)] font-normal">/100</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-0.5">
          <span className={`text-[13px] max-md:text-[11px] font-[300] text-[var(--text-secondary)] dark:text-[#94a3b8] text-left ${isLoading ? 'animate-blink' : ''}`}>
            {isLoading ? '--%' : `${progressValue}%`} ke level selanjutnya
          </span>
          <div className="flex items-center gap-[3px] max-md:gap-1">
            <div className={`flex-1 h-[10px] max-md:h-[8px] ${isLesson ? 'bg-[rgba(251,146,60,0.1)]' : 'bg-[rgba(18,118,109,0.1)] dark:bg-[rgba(148,163,184,0.15)]'} rounded-full overflow-hidden relative`}>
              <div
                className={`practice-progress-fill h-full ${isLesson ? 'bg-gradient-to-r from-[#fb923c] to-[#f97316] shadow-[0_1px_3px_rgba(251,146,60,0.3)]' : 'bg-gradient-to-r from-[#14b8a6] via-[#0d9488] to-[#0f766e] shadow-[0_1px_3px_rgba(18,118,109,0.3)]'} rounded-full transition-[width_0.4s_cubic-bezier(0.4,0,0.2,1)] relative overflow-hidden ${isLoading ? 'animate-blink' : ''}`}
                style={{ width: isLoading ? '0%' : `${progressPercentage}%` }}
              >
                {!isLoading && (
                  <>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" aria-hidden />
                    <div className="absolute top-0 left-0 w-[30%] h-full rounded-full bg-gradient-to-r from-white/30 to-transparent animate-shine" aria-hidden />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* changed by Cursor: button start dipindah ke bawah card */}
      <button
        className="practice-start-button w-full py-[8px] px-[12px] bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[#1d4746] border-none rounded-[8px] text-[14px] leading-none font-bold cursor-pointer mt-auto transition-all duration-300 shadow-[0_2px_4px_rgba(252,179,25,0.2)] relative overflow-hidden whitespace-nowrap hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:-translate-y-px active:translate-y-0 active:scale-[0.98] active:shadow-[0_1px_2px_rgba(252,179,25,0.2)] before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] before:transition-[left] before:duration-500 hover:before:left-full"
        onClick={onStart}
      >
        Start
      </button>
    </motion.div>
  );
};

export default WritingPracticeButton;
